import React, { useState, useEffect, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';
import Cookies from 'js-cookie';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import api from '../../../config/api';
import { Link } from 'react-router-dom';


const EmployeeDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);
    const [profile, setProfile] = useState(null);
    const [profileLoading, setProfileLoading] = useState(true);

    const [loadingReviews, setLoadingReviews] = useState(true);
    const [reviewsError, setReviewsError] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [availableYears, setAvailableYears] = useState([]);
    const [filteredReviews, setFilteredReviews] = useState([]);

    useEffect(() => {
        const fetchProfile = async () => {
            setProfileLoading(true);
            try {
                const response = await api.get('/api/users/me');
                setProfile(response.data);
            } catch (err) {
                setProfile(null);
            } finally {
                setProfileLoading(false);
            }
        };
        fetchProfile();
    }, []);

    useEffect(() => {
        const fetchAnalytics = async () => {
            if (!profile?.employee?.id) return;
            setLoading(true);
            try {
                const currentYear = new Date().getFullYear();
                const response = await api.get('/api/performance-analytics', {
                    params: { 
                        employee: profile.employee.id,
                        year: currentYear
                    }
                });
                setAnalytics(response.data);
            } catch (err) {
                setAnalytics(null);
            } finally {
                setLoading(false);
            }
        };
        if (profile && !profileLoading) {
            fetchAnalytics();
        }
    }, [profile, profileLoading]);

    useEffect(() => {
        const fetchReviews = async () => {
            setLoadingReviews(true);
            setReviewsError(null);
            try {
                const response = await api.get('/api/users/me/');
                const completed = (Array.isArray(response.data.employee?.goals) ? response.data.employee.goals : [])
                    .filter(g => (g.status === 'complete' || g.status === 'completed') && Number(g.hod_score) > 0);
                completed.sort((a, b) => new Date(b.updated_at || b.completed_at || b.created_at) - new Date(a.updated_at || a.completed_at || a.created_at));
                setReviews(completed);
                
                const years = Array.from(new Set(completed.map(g => g.year).filter(y => y !== undefined && y !== null))).sort((a, b) => b - a);
                setAvailableYears(years);
            } catch (err) {
                setReviewsError('Failed to fetch recent reviews.');
            } finally {
                setLoadingReviews(false);
            }
        };
        fetchReviews();
    }, []);

    useEffect(() => {
        if (reviews.length > 0) {
            const filtered = reviews.filter(review => {
                if (review.year !== undefined && review.year !== null) {
                    return review.year === selectedYear;
                }
                const reviewDate = new Date(review.updated_at || review.completed_at || review.created_at);
                return reviewDate.getFullYear() === selectedYear;
            });
            setFilteredReviews(filtered);
        }
    }, [reviews, selectedYear]);

    const stats = analytics?.overall_statistics || {};
    const timeliness = analytics?.overall_statistics || {};

    const quarterlyTrendsData = useMemo(() => {
        if (!analytics?.quarterly_trends) return [];
        return Object.entries(analytics.quarterly_trends).map(([quarter, stats]) => ({
            quarter,
            ...stats
        }));
    }, [analytics]);

    return (
        <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
            <Topbar />
            <Sidebar />
            <div className="page-wrapper">
                <div className="content pb-0">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
                        <div>
                            <h4 className="fw-bold mb-1">Performance Dashboard</h4>
                            <p className="text-muted mb-0 fs-13">Overview of employee performance metrics and KPIs</p>
                        </div>
                    </div>

                    <div className="row g-4 mb-4">
                        <div className="col-12 col-md-4">
                            <div className="card shadow-sm border-0 h-100">
                                <div className="card-body">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="avatar avtar-lg bg-primary-subtle text-primary rounded-circle">
                                            <i className="ti ti-star fs-24" />
                                        </div>
                                        <div>
                                            <h6 className="text-muted mb-1 fs-13">Completed KPIs</h6>
                                            {loading ? <Skeleton width={60} /> : <h4 className="fw-bold mb-0">{stats.completed_goals || 0} / {stats.total_goals || 0}</h4>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-md-4">
                            <div className="card shadow-sm border-0 h-100">
                                <div className="card-body">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="avatar avtar-lg bg-success-subtle text-success rounded-circle">
                                            <i className="ti ti-trending-up fs-24" />
                                        </div>
                                        <div>
                                            <h6 className="text-muted mb-1 fs-13">Avg Performance</h6>
                                            {loading ? <Skeleton width={60} /> : <h4 className="fw-bold mb-0">{stats.average_progress ? `${stats.average_progress}%` : '0%'}</h4>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-md-4">
                            <div className="card shadow-sm border-0 h-100">
                                <div className="card-body">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="avatar avtar-lg bg-warning-subtle text-warning rounded-circle">
                                            <i className="ti ti-clock fs-24" />
                                        </div>
                                        <div>
                                            <h6 className="text-muted mb-1 fs-13">KPIs Completion Timeliness</h6>
                                            {loading ? <Skeleton width={120} height={20} /> : (
                                                <div className="d-flex gap-2 flex-wrap">
                                                    <span className="badge bg-success-subtle text-success border border-success-subtle">
                                                        On Time: {timeliness.on_time_rate != null ? timeliness.on_time_rate : 0}%
                                                    </span>
                                                    <span className="badge bg-primary-subtle text-primary border border-primary-subtle">
                                                        Early: {timeliness.early_rate != null ? timeliness.early_rate : 0}%
                                                    </span>
                                                    <span className="badge bg-danger-subtle text-danger border border-danger-subtle">
                                                        Late: {timeliness.late_rate != null ? timeliness.late_rate  : 0}%
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row g-4 mb-4">
                        <div className="col-12">
                            <div className="card shadow-sm border-0">
                                <div className="card-header d-flex align-items-center justify-content-between">
                                    <h5 className="fw-bold mb-0">Quarterly Performance Trends</h5>
                                    <button className="btn btn-sm btn-icon btn-light border"><i className="ti ti-dots-vertical" /></button>
                                </div>
                                <div className="card-body">
                                    {loading ? (
                                        <Skeleton height={300} />
                                    ) : (
                                        <div style={{ width: '100%', height: 300 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <AreaChart data={quarterlyTrendsData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                                                    <defs>
                                                        <linearGradient id="colorQuarterly" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.8}/>
                                                            <stop offset="95%" stopColor="#4F46E5" stopOpacity={0}/>
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f5" />
                                                    <XAxis dataKey="quarter" tick={{ fontSize: 12, fill: '#6b7280' }} />
                                                    <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12, fill: '#6b7280' }} />
                                                    <Tooltip formatter={(value) => `${value}%`} />
                                                    <Area 
                                                        type="monotone" 
                                                        dataKey="completion_rate" 
                                                        stroke="#4F46E5" 
                                                        fillOpacity={1} 
                                                        fill="url(#colorQuarterly)" 
                                                    />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card shadow-sm border-0 mb-4">
                        <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <h5 className="fw-bold mb-0">Recent Reviews</h5>
                            <Link to="/employee/goal-reviews" className="btn btn-sm btn-primary d-flex align-items-center gap-1">
                                <i className="ti ti-list" /> View All
                            </Link>
                        </div>
                        
                        <div className="card-body p-0">
                            {loadingReviews ? (
                                <div className="text-center py-5 text-muted">
                                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                                    Loading...
                                </div>
                            ) : reviewsError ? (
                                <div className="text-center py-5 text-danger">{reviewsError}</div>
                            ) : (!filteredReviews || filteredReviews.length === 0) ? (
                                <div className="text-center py-5 text-muted">
                                    <i className="ti ti-clipboard-off fs-36 d-block mb-2 opacity-50" />
                                    No recent reviews found for {selectedYear}.
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th className="ps-4">Activity / KPI</th>
                                                <th>Performance Standard / Target</th>
                                                <th>Metric</th>
                                                <th>Self Score</th>
                                                <th className="pe-4">HOD Score</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredReviews.slice(0, 5).map((review, idx) => (
                                                <tr key={review.id || idx}>
                                                    <td className="ps-4">
                                                        <div className="text-dark fw-medium text-truncate" style={{ maxWidth: '220px' }}>
                                                            {review.activity || '-'}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="text-muted text-truncate" style={{ maxWidth: '300px' }}>
                                                            {review.performance_standard || '-'}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="text-muted text-truncate" style={{ maxWidth: '300px' }}>
                                                            {review.metric || '-'}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-1 fs-12">
                                                            {review.score != null ? review.score : '-'}
                                                        </span>
                                                    </td>
                                                    <td className="pe-4">
                                                        <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1 fs-12">
                                                            {review.hod_score != null ? review.hod_score : '-'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
                        <p className="text-dark mb-0">
                            2026 © <span className="link-primary">Casoe Technologies</span> All Rights Reserved
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmployeeDashboard;
